import { PlusIcon } from "@heroicons/react/outline";
import { isEmpty } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  RouteComponentProps,
  useHistory,
  useParams
} from "react-router-dom";
import { useAuthentication } from "../../../context/authentication";
import { useToast } from "../../../context/toast";
import { useWorkspaces } from "../../../context/workspaces";
import AppShell from "../../../layout/AppShell";
import { ApiResponse, fetchApi } from "../../../utils/api";
import { avatarsApi } from "../../../utils/constants";

interface AcceptInvitationParams {
  invitationCode: string;
}

export const AcceptInvitation: React.FC<RouteComponentProps> = (props) => {
  const history = useHistory();
  const { addToast } = useToast();
  const { workspaces, setCurrentWorkspaceId, loadWorkspaces } = useWorkspaces();
  const { user, authenticationState } = useAuthentication();
  const params: AcceptInvitationParams = useParams();
  const [invitationStatus, setInvitationStatus] =
    useState<InvitationStatus>("checking");
  const [invitationData, setInvitationData] = useState<
    GetInvitationResponse | undefined
  >();

  useEffect(() => {
    if (authenticationState !== "authenticated") return;

    (async () => {
      const response = await fetchApi(
        `/api/invitation/${params.invitationCode}`
      );
      const apiResponse: ApiResponse<GetInvitationResponse> =
        await response.json();

      if (apiResponse.status) {
        if (workspaces[apiResponse.data.workspace_uuid]) {
          setCurrentWorkspaceId(invitationData?.workspace_uuid as string);

          return history.push("/workspace/settings/members");
        }

        setInvitationStatus("valid");
        setInvitationData(apiResponse.data);
      } else {
        setInvitationStatus("invalid");
      }
    })();
  }, [
    authenticationState,
    history,
    invitationData?.workspace_uuid,
    params.invitationCode,
    setCurrentWorkspaceId,
    workspaces
  ]);

  const acceptOrRejectInvitation = useCallback(
    async (status: "accepted" | "rejected") => {
      if (invitationStatus !== "valid") return;

      const response = await fetchApi(
        `/api/invitation/${params.invitationCode}`,
        {
          method: "PUT",
          body: JSON.stringify({ status })
        }
      );

      if (response.ok) {
        if (status === "accepted") {
          await loadWorkspaces();
          setCurrentWorkspaceId(invitationData?.workspace_uuid as string);
        }

        return history.push("/workspace/dashboard");
      }

      return addToast({
        type: "error",
        message: "Failed to accept invitation!"
      });
    },
    [
      addToast,
      history,
      invitationData?.workspace_uuid,
      invitationStatus,
      params.invitationCode,
      loadWorkspaces,
      setCurrentWorkspaceId
    ]
  );

  if (authenticationState === "unauthenticated") return null;

  return (
    <AppShell
      {...props}
      currentPage="Workspace invitation"
      isLoading={invitationStatus === "checking"}
    >
      <div
        className="flex flex-row items-center justify-center dark:bg-gray-900 dark:text-white"
        style={{
          minHeight: "calc(100vh - 49px)",
          maxHeight: "calc(100vh - 49px)"
        }}
      >
        {invitationStatus === "invalid" && (
          <InvitationMessageBox
            title="Sorry, we could not find the invitation."
            footerItems={[
              <Link
                to="/"
                className="mt-2 inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
              >
                Back to Home
              </Link>
            ]}
          />
        )}

        {invitationStatus === "valid" && invitationData && (
          <InvitationMessageBox
            title={
              <>
                <span className="font-bold text-hotspotty-400">
                  {invitationData.invited_by}
                </span>{" "}
                invited you to collaborate
              </>
            }
            message={
              <p>
                By accepting this invitation you will be able to join the
                workspace{" "}
                <span className="font-bold text-hotspotty-400">
                  {invitationData.workspace}
                </span>
                .
              </p>
            }
            header={
              <div className="flex items-center justify-center mb-6 space-x-4">
                <img
                  className="h-14 w-14 rounded-full"
                  alt="Inviting user avatar"
                  src={
                    invitationData.avatar_url ||
                    stringifyUrl({
                      url: `${avatarsApi}/initials/${invitationData.invited_by}.svg`,
                      query: { w: 56, h: 56 }
                    })
                  }
                />

                <PlusIcon className="h-6 w-6" />

                <img
                  className="h-14 w-14 rounded-full"
                  alt="Invited user avatar"
                  src={
                    user.avatar_url ||
                    stringifyUrl({
                      url: `${avatarsApi}/initials/${user.name}.svg`,
                      query: { w: 56, h: 56 }
                    })
                  }
                />
              </div>
            }
            footerItems={[
              <button
                onClick={() => acceptOrRejectInvitation("rejected")}
                type="button"
                className="bg-white dark:bg-gray-800 px-2.5 py-1.5 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
              >
                Decline
              </button>,
              <button
                onClick={() => acceptOrRejectInvitation("accepted")}
                type="button"
                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
              >
                Accept Invitation
              </button>
            ]}
          />
        )}
      </div>
    </AppShell>
  );
};

//
// Utils
//

interface GetInvitationResponse {
  available: boolean;
  avatar_url: string | null;
  invited_by: string;
  workspace: string;
  workspace_uuid: string;
}

type InvitationStatus = "checking" | "valid" | "invalid";

const InvitationMessageBox = ({
  title,
  message,
  header,
  footerItems
}: {
  title?: string | React.ReactNode;
  message?: string | React.ReactNode;
  header?: React.ReactNode;
  footerItems?: React.ReactNode[];
}) => (
  <div className="sm:shadow sm:rounded-lg max-w-md">
    <div className="sm:bg-gray-50 sm:dark:bg-gray-800 sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        {header}

        {title && (
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            {title}
          </h3>
        )}

        {message && (
          <div className="mt-2 max-w-xl text-sm text-gray-500 dark:text-gray-300">
            {message}
          </div>
        )}

        {footerItems && !isEmpty(footerItems) && (
          <div className="flex mt-6 space-x-4">
            <div className="flex-1" />
            {footerItems.map((Item, idx) => (
              <div key={idx}>{Item}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);
